import { Children, PropsWithChildren, useCallback, useEffect, useMemo } from 'react'

import { I18nProvider } from '../types'
import { useSafeSetState } from '../util/hooks'
import { TranslationContext } from './TranslationContext'

interface Props {
  locale?: string
  i18nProvider?: I18nProvider
}

declare global {
  interface Window {
    translate: any
    locale?: string
  }
}

/**
 * Creates a translation context, available to its children
 *
 * @example
 *     const MyApp = () => (
 *         <Provider store={store}>
 *             <TranslationProvider i18nProvider={i18nProvider}>
 *                 <!-- Child components go here -->
 *             </TranslationProvider>
 *         </Provider>
 *     );
 */
const TranslationProvider = (props: PropsWithChildren<Props>) => {
  const { i18nProvider, children } = props

  const [state, setState] = useSafeSetState<any>({
    locale: i18nProvider ? i18nProvider.getLocale() : 'en',
    i18nProvider,
  })

  const setLocale = useCallback(
    (newLocale: string) =>
      setState({
        locale: newLocale,
        i18nProvider,
      }),
    [i18nProvider, setState]
  )

  // Allow locale modification by including setLocale in the context
  // This can't be done in the initial state because setState doesn't exist yet
  const value = useMemo(
    () => ({
      ...state,
      setLocale,
    }),
    [setLocale, state]
  )

  useEffect(() => {
    window.translate = i18nProvider?.translate
    const locale = state.locale || i18nProvider?.getLocale() || 'en'
    const htmlLang = locale.replace('_', '-')
    window.locale = htmlLang

    // Set lang attribute on HTML element for soft hyphenation support
    // This applies to all child elements automatically (inherited attribute)
    // This ensures soft hyphens work correctly across the entire application
    if (typeof document !== 'undefined') {
      document.documentElement.lang = htmlLang
    }
  }, [i18nProvider, state.locale])

  return <TranslationContext.Provider value={value}>{Children.only(children)}</TranslationContext.Provider>
}

export default TranslationProvider
